import { CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Alert, Button, Col, ColorPicker, Form, type FormInstance, Input, Row, Typography, Upload } from 'antd';
import type { Color } from 'antd/es/color-picker';
import type React from 'react';
import { useState } from 'react';
import logo from '../../../assets/logo.svg';
import type { ThemeInput } from '../../../graphql/generated/graphql.ts';
import { blobToDataUrl } from '../../../utils/helpers.ts';

export const ThemeForm: React.FC<{
  form: FormInstance<ThemeInput>;
  onFinish: (values: ThemeInput) => void;
  initialValues?: Partial<ThemeInput>;
}> = ({ form, onFinish, initialValues }) => {
  const [fileError, setFileError] = useState('');

  return (
    <Form layout="vertical" form={form} onFinish={() => onFinish(form.getFieldsValue(true))} initialValues={initialValues}>
      <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true }]}>
        <Input autoFocus />
      </Form.Item>
      <Row gutter={[16, 0]}>
        <Col span={4}>
          <Form.Item name="colorPrimary" label="Primärfarbe" getValueFromEvent={(a: Color) => a.toHexString()}>
            {/* getPopupContainer ensures the ColorPicker panel renders inside the modal container,
                allowing the hex input field to receive focus (fixes modal focus trap issue) */}
            <ColorPicker getPopupContainer={node => node.parentElement?.parentElement || document.body} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="controlItemBgActive" label="BG Aktiv" getValueFromEvent={(a: Color) => a.toHexString()}>
            <ColorPicker getPopupContainer={node => node.parentElement?.parentElement || document.body} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="controlItemBgActiveHover" label="BG Hover" getValueFromEvent={(a: Color) => a.toHexString()}>
            <ColorPicker getPopupContainer={node => node.parentElement?.parentElement || document.body} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="colorBgHeader" label="BG Header" getValueFromEvent={(a: Color) => a.toHexString()}>
            <ColorPicker getPopupContainer={node => node.parentElement?.parentElement || document.body} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="colorBgTrigger" label="BG Trigger" getValueFromEvent={(a: Color) => a.toHexString()}>
            <ColorPicker getPopupContainer={node => node.parentElement?.parentElement || document.body} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={8}>
          <Upload
            showUploadList={false}
            maxCount={1}
            beforeUpload={file => {
              return new Promise((resolve, reject) => {
                if (file.size > 80000 /* 80 KB */) {
                  setFileError('Logo darf nicht größer als 80KB sein');
                  reject();
                  return;
                }

                if (
                  file.type !== 'image/jpeg' &&
                  file.type !== 'image/png' &&
                  file.type !== 'image/jpg' &&
                  file.type !== 'image/svg' &&
                  file.type !== 'image/svg+xml'
                ) {
                  setFileError('Logo ist kein JPG, PNG oder SVG');
                  reject();
                  return;
                }

                setFileError('');
                resolve();
              });
            }}
            customRequest={async info => {
              const dataUrl = await blobToDataUrl(info.file as Blob);
              form.setFieldValue('logoData', dataUrl);
            }}
          >
            <Button icon={<CloudUploadOutlined />}>Logo hochladen</Button>
          </Upload>
          {fileError && (
            <div>
              <Typography.Text type="danger">{fileError}</Typography.Text>
            </div>
          )}
        </Col>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldValue }) => (
            <>
              <Col span={8}>
                <img
                  src={getFieldValue('logoData') ? getFieldValue('logoData') : logo}
                  alt="Logo Preview"
                  className={css`
                    width: 200px;
                  `}
                />
              </Col>
              <Col span={8}>
                <Button danger icon={<DeleteOutlined />} hidden={!getFieldValue('logoData')} onClick={() => setFieldValue('logoData', '')}>
                  Logo löschen
                </Button>
              </Col>
            </>
          )}
        </Form.Item>
      </Row>
      <p />
      <Alert
        type="info"
        showIcon
        title={
          <ul style={{ margin: 0 }}>
            <li>Das Logo muss ein .jpg, .png oder .svg sein</li>
            <li>Empfohlenes Format: SVG</li>
            <li>Empfohlene Auflösung: 200x30</li>
            <li>Max. Dateigröße: 80KB</li>
          </ul>
        }
      />
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
    </Form>
  );
};
